import { Dispatch, KeyboardEvent, RefObject, SetStateAction } from 'react';

import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';

type Props = {
  lockActiveStateProps: boolean;
  lockActiveState: boolean;
  menuOpen: boolean;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  selectedOptionsRef: RefObject<HTMLDivElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  selectRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  selectedOptions: TReactSelectOption[];
  setSearchValue: Dispatch<SetStateAction<string>>;
  setSelectedOptions: Dispatch<SetStateAction<TReactSelectOption[]>>;
  onAddNewSelect?: (inputValue: string) => void;
  selectableOptions: TReactSelectOption[];
  allOptions: TReactSelectOption[];
  selectFirstSearchOptionOnEnter?: boolean;
  clearInputOnEnter?: boolean;
};

const selectKeyDownCapture =
  ({
    selectedOptionsRef,
    setMenuOpen,
    lockActiveStateProps,
    menuOpen,
    lockActiveState,
    menuRef,
    inputRef,
    selectRef,
    selectedOptions,
    setSelectedOptions,
    selectableOptions,
    onAddNewSelect,
    allOptions,
    selectFirstSearchOptionOnEnter,
    clearInputOnEnter,
    setSearchValue,
  }: Props) =>
  (e: KeyboardEvent) => {
    if (lockActiveStateProps || !lockActiveState) return;

    if (e.key === 'Escape') {
      if (menuOpen) {
        e.stopPropagation();
        setMenuOpen(false);
      } else if (inputRef.current === document.activeElement) {
        e.stopPropagation();
        inputRef.current?.blur();
      }
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      setMenuOpen(true);
      const menuOptions = Array.from(document.querySelectorAll('[data-menu-list-option]'));
      if (!menuOptions.length) return;
      const isUp = e.key === 'ArrowUp';
      const isDown = e.key === 'ArrowDown';

      // if active remove option button, remove active state to only have one active thing a time
      const activeRemoveOption = selectedOptionsRef.current?.querySelector('[data-remove-option-active="true"]');
      if (activeRemoveOption) {
        activeRemoveOption.setAttribute('data-remove-option-active', 'false');
      }

      const activeMenuOption = document.querySelector('[data-menu-list-option-active="true"]');
      let newActiveMenuOption: HTMLElement | null = null;

      if (activeMenuOption) {
        const currentIdx = Number(activeMenuOption.getAttribute('data-menu-list-option-idx') || '0');
        activeMenuOption.setAttribute('data-menu-list-option-active', 'false');

        // wrap (cycle) using modulo so Up/Down continues around the list
        const len = menuOptions.length;
        let newIdx: number;
        if (isUp) {
          newIdx = (currentIdx - 1 + len) % len;
        } else {
          newIdx = (currentIdx + 1) % len;
        }
        newActiveMenuOption = menuOptions[newIdx] as HTMLElement;
      } else {
        // when no active option, start at first on Down or last on Up
        if (isDown) {
          newActiveMenuOption = menuOptions[0] as HTMLElement;
        } else if (isUp) {
          newActiveMenuOption = menuOptions[menuOptions.length - 1] as HTMLElement;
        }
      }
      if (newActiveMenuOption) {
        newActiveMenuOption.setAttribute('data-menu-list-option-active', 'true');
        // ensure active option is visible in menu by scrolling in menuRef
        const menuEl = menuRef.current;
        if (menuEl) {
          const optionOffsetTop = newActiveMenuOption.offsetTop;
          const menuScrollHeight = menuEl.scrollHeight;
          const optionHeight = newActiveMenuOption.offsetHeight;
          menuEl.scrollTo({
            top: Math.min(optionOffsetTop - optionHeight / 2, menuScrollHeight),
          });
        }
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
      if (inputRef.current?.value) return; // only when no input value

      const removableOptions = Array.from(
        selectedOptionsRef.current?.querySelectorAll('[data-remove-option]') || { length: 0 }
      );
      if (!removableOptions.length) return;
      const isLeft = e.key === 'ArrowLeft';
      const isRight = e.key === 'ArrowRight';

      // if active dropdown option, remove active state to only have one active thing a time
      const activeMenuOption = document.querySelector('[data-menu-list-option-active="true"]');
      if (activeMenuOption) {
        activeMenuOption.setAttribute('data-menu-list-option-active', 'false');
      }

      const activeRemoveOption = selectedOptionsRef.current?.querySelector('[data-remove-option-active="true"]');
      let newActiveRemoveOption: HTMLElement | null = null;
      if (activeRemoveOption) {
        const currentIdx = Number(activeRemoveOption.getAttribute('data-remove-option-idx') || '0');
        activeRemoveOption.setAttribute('data-remove-option-active', 'false');
        let newIdx = currentIdx;
        if (isLeft) {
          newIdx = Math.max(0, currentIdx - 1);
        } else if (isRight) {
          if (currentIdx === removableOptions.length - 1) {
            newIdx = -1;
          } else {
            newIdx = Math.min(removableOptions.length - 1, currentIdx + 1);
          }
        }
        newActiveRemoveOption = removableOptions[newIdx] as HTMLElement;
      } else {
        if (isLeft) {
          newActiveRemoveOption = removableOptions[removableOptions.length - 1] as HTMLElement;
        }
      }
      if (newActiveRemoveOption) {
        newActiveRemoveOption.setAttribute('data-remove-option-active', 'true');
      } else {
        activeRemoveOption?.setAttribute('data-remove-option-active', 'false');
      }
      e.stopPropagation();
      e.preventDefault();

      // scroll remove button into view
      const selectEl = selectRef.current;
      if (selectEl && newActiveRemoveOption) {
        const optionOffsetLeft = newActiveRemoveOption.offsetLeft;
        const optionOffsetWidth = newActiveRemoveOption.offsetWidth;
        const selectScrollWidth = selectEl.scrollWidth;
        selectEl.scrollTo({
          left: Math.min(optionOffsetLeft - optionOffsetWidth - 50, selectScrollWidth),
        });
      }
    }

    if (e.key === 'Backspace') {
      const activeRemoveOption = selectedOptionsRef.current?.querySelector('[data-remove-option-active="true"]');
      if (activeRemoveOption) {
        const value = activeRemoveOption.getAttribute('data-remove-option-value');
        if (value) {
          e.stopPropagation();
          e.preventDefault();
          setSelectedOptions((prev) => prev.filter((so) => so.value.toString() !== value.toString()));
        }
      }
      if (!activeRemoveOption && !inputRef.current?.value && selectedOptions.length) {
        // remove last option
        e.stopPropagation();
        e.preventDefault();
        setSelectedOptions((prev) => prev.slice(0, prev.length - 1));
      }

      return;
    }

    if (e.key === 'Enter') {
      const activeMenuOption = document.querySelector('[data-menu-list-option-active="true"]');
      if (activeMenuOption) {
        const activeIsAddOption = activeMenuOption.hasAttribute('data-menu-list-option-add-new');

        if (activeIsAddOption) {
          // handle add new option
          e.stopPropagation();
          e.preventDefault();
          onAddNewSelect?.(inputRef.current?.value || '');
          activeMenuOption.setAttribute('data-menu-list-option-active', 'false');
          return;
        }

        const value = activeMenuOption.getAttribute('data-menu-list-option-value');
        const label = activeMenuOption.textContent;

        if (value && label) {
          e.stopPropagation();
          e.preventDefault();

          const idxOfCurrent = parseInt(activeMenuOption.getAttribute('data-menu-list-option-idx') ?? '0');
          const nextIdx = idxOfCurrent + 1 >= selectableOptions.length - 1 ? 0 : idxOfCurrent + 1;
          const nextOption = selectableOptions?.[nextIdx];

          activeMenuOption.setAttribute('data-menu-list-option-active', 'false');

          const option = allOptions.find((o) => o.value.toString() === value.toString());
          if (option) {
            setSelectedOptions((prev) => {
              const alreadySelected = prev.find((previousOption) => previousOption.value === option.value);
              if (alreadySelected) {
                return prev.filter((po) => po.value !== option.value);
              }
              return [...prev, option];
            });
          }

          // let options append and then scroll
          setTimeout(() => {
            selectRef?.current?.scrollTo({
              left: selectRef?.current?.scrollWidth,
              behavior: 'smooth',
            });
          }, 0);

          if (clearInputOnEnter) {
            // reset/clear input
            setSearchValue('');
            if (inputRef.current) {
              inputRef.current.value = '';
            }
            menuRef?.current?.scrollTo({ top: 0 });
          } else if (nextOption) {
            // select the next available option in the menu after selecting one
            // else go to first option
            const nextOptionElement = document.querySelector(`[data-menu-list-option-idx="${nextIdx}"]`);
            if (nextOptionElement && nextOptionElement instanceof HTMLElement) {
              nextOptionElement.setAttribute('data-menu-list-option-active', 'true');

              const menuEl = menuRef?.current;
              if (menuEl) {
                const optionOffsetTop = nextOptionElement.offsetTop;
                const menuScrollHeight = menuEl.scrollHeight;
                const optionHeight = nextOptionElement.offsetHeight;
                menuEl.scrollTo({
                  top: Math.min(optionOffsetTop - optionHeight / 2, menuScrollHeight),
                });
              }
            }
          }
        }
      } else {
        // if search value and no selectableOptions length, trigger add
        if (inputRef?.current?.value.trim().length && !selectableOptions.length && onAddNewSelect) {
          onAddNewSelect?.(inputRef.current.value);

          // else if input is focused, we have text and selectable options, then select the first one
        } else if (
          selectFirstSearchOptionOnEnter &&
          document.activeElement === inputRef.current &&
          inputRef?.current?.value.trim().length &&
          selectableOptions?.[0]
        ) {
          // if input is active, we have input text value, and a matched first selectableOptions

          const optionToAdd = selectableOptions?.[0];
          setSelectedOptions((prev) => {
            const alreadySelected = prev.find((previousOption) => previousOption.value === optionToAdd.value);
            if (alreadySelected) {
              return prev;
            }
            return [...prev, optionToAdd];
          });
          // let options append and then scroll
          setTimeout(() => {
            selectRef?.current?.scrollTo({
              left: selectRef?.current?.scrollWidth,
              behavior: 'smooth',
            });
          }, 0);

          if (clearInputOnEnter) {
            // reset/clear input
            setSearchValue('');
            if (inputRef.current) {
              inputRef.current.value = '';
            }
            menuRef?.current?.scrollTo({ top: 0 });
          }
        }
      }
      return;
    }
  };

export default selectKeyDownCapture;
